import {useRegionConfig} from '@frontend/hooks/useRegionConfig'
import numeral from 'numeral'
import {
    type ChangeEvent,
    type ClipboardEvent,
    type FocusEvent,
    type InputEvent,
    type KeyboardEvent,
    type MouseEvent,
} from 'react'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {InputAddonText} from 'swayok-react-mdb-ui-kit/components/Input/InputAddonText'
import type {InputProps} from 'swayok-react-mdb-ui-kit/components/Input/InputTypes'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {NumbersService} from 'swayok-react-mdb-ui-kit/services/NumbersService'

export interface MoneyInputProps extends Omit<
    InputProps,
    'onChange' | 'onPaste' | 'onBeforeInput' | 'allowedChars' | 'children'
> {
    onChange: (
        e: ChangeEvent<HTMLInputElement>
            | FocusEvent<HTMLInputElement>
            | KeyboardEvent<HTMLInputElement>,
        formattedValue: string,
        cleanValue: number | null
    ) => void
    children?: never
    numeralFormat?: string
    minCursorPosition?: number
    // Кол-во цифр в целой части вводимого числа.
    maxLength?: number
}

// Поле ввода количества денег.
export function MoneyInput(props: MoneyInputProps) {

    const regionConfig = useRegionConfig()

    // Переименование переменных требуется, чтобы в eventHandlers точно не использовались
    // никакие значения из props напрямую.
    const {
        value: propsValue,
        numeralFormat = regionConfig.moneyInput.numeralFormat,
        minCursorPosition = 0,
        onChange: propsOnChange,
        onClick: propsOnClick,
        onFocus: propsOnFocus,
        onKeyDown: propsOnKeyDown,
        maxLength: propsMaxLength,
        inputMode = 'numeric',
        ...otherProps
    } = props

    // Обработка нажатия на кнопку клавиатуры.
    const onKeyDown = useEventCallback((
        e: KeyboardEvent<HTMLInputElement>
    ) => {
        if (!e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey) {
            const input = e.currentTarget
            const action: string = e.key
            switch (action) {
                case 'Backspace':
                    if (input.selectionStart === input.selectionEnd) {
                        const endPosition: number = (input.selectionStart ?? 0) - 1
                        let nextCursorPosition: number = endPosition
                        if (isHardcodedCharacter(input.value[nextCursorPosition])) {
                            if (nextCursorPosition > minCursorPosition) {
                                nextCursorPosition--
                                while (
                                    isHardcodedCharacter(input.value[nextCursorPosition])
                                    && nextCursorPosition > minCursorPosition
                                ) {
                                    nextCursorPosition--
                                }
                            }
                            const newValue: string = input.value.substring(0, nextCursorPosition)
                                + input.value.substring(endPosition)
                            const formattedValue: string = formatValue(
                                newValue,
                                numeralFormat
                            )
                            propsOnChange(e, formattedValue, cleanValue(newValue))
                            e.preventDefault()
                        } else if (input.selectionStart === input.value.length) {
                            const newValue: string = input.value.substring(0, input.value.length - 1)
                            const formattedValue: string = formatValue(
                                newValue,
                                numeralFormat
                            )
                            propsOnChange(e, formattedValue, cleanValue(newValue))
                            e.preventDefault()
                        }
                    }
                    break
                case 'ArrowLeft':
                case 'ArrowRight': {
                    const shift: number = action === 'ArrowLeft' ? -1 : 1
                    let cursorPosition: number = ((action === 'ArrowLeft' ? input.selectionStart : input.selectionEnd) ?? 0) + shift
                    while (
                        cursorPosition < input.value.length
                        && cursorPosition > 0
                        && isHardcodedCharacter(input.value[cursorPosition])
                    ) {
                        cursorPosition += shift
                    }
                    if (action === 'ArrowLeft' && cursorPosition <= minCursorPosition) {
                        cursorPosition = minCursorPosition
                    } else if (action === 'ArrowRight' && cursorPosition >= input.value.length) {
                        cursorPosition = input.value.length
                    } else {
                        cursorPosition = findCursorPosition(
                            input.value,
                            cursorPosition,
                            minCursorPosition
                        )
                    }
                    input.setSelectionRange(cursorPosition, cursorPosition)
                    e.preventDefault()
                    break
                }
            }
        }
        propsOnKeyDown?.(e)
    })

    // Проверка лимита целой части.
    const onBeforeInput = useEventCallback((
        e: InputEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (!propsMaxLength) {
            // Нет лимита: пропускаем.
            return
        }

        const char: string = e.data
        if (!/^\d$/.test(char)) {
            // Не цифра.
            if (e.currentTarget.selectionStart === 0) {
                // Первый символ, введенный в поле ввода, не цифра: игнорируем ввод.
                e.preventDefault()
            }
            return
        }

        if (e.currentTarget.value.length === 0) {
            // Первая цифра: пропускаем без проверок.
            return
        }

        // Вычисление нового числа (строка).
        const prevValue: string = e.currentTarget.value
        const newValue = prevValue.substring(0, e.currentTarget.selectionStart!)
            + char + prevValue.substring(e.currentTarget.selectionEnd!, prevValue.length)

        // Конвертируем вычисленное число (string) в число (number).
        const intPart: number | null = numeral(newValue).value()
        if (!intPart) {
            // Не число? Не знаю, как это может случиться, но лучше подстраховаться.
            e.preventDefault()
            return
        }
        if (Math.floor(intPart).toString().length > propsMaxLength) {
            // Перебор чисел в целой части: игнорируем ввод.
            e.preventDefault()
            return
        }
    })

    // Изменение значения.
    const onChange = useEventCallback((
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = formatValue(e.currentTarget.value, numeralFormat)
        let nextCursorPosition = findCursorPosition(
            value,
            e.currentTarget.selectionStart ?? minCursorPosition,
            minCursorPosition
        )
        // Если добавлен разделитель тысячных, то нужно сместить позицию курсора.
        if (value.length !== e.currentTarget.value.length) {
            nextCursorPosition += value.length - e.currentTarget.value.length
        }
        e.currentTarget.value = value
        e.currentTarget.setSelectionRange(nextCursorPosition, nextCursorPosition)
        propsOnChange(e, value, cleanValue(e.currentTarget.value))
    })

    // Вставка значения из буфера обмена.
    const onPaste = useEventCallback((
        e: ClipboardEvent<HTMLInputElement>
    ) => {
        if (e.currentTarget.value === '') {
            // Нет значения: разрешаем вставку.
            return
        }
        if (
            e.currentTarget.selectionStart === 0
            && e.currentTarget.selectionEnd === e.currentTarget.value.length
        ) {
            // Полная замена текущего значения: разрешаем вставку.
            return
        }
        // В любом другом случае - запрещаем вставку.
        e.preventDefault()
    })

    // Фокусировка в поле ввода.
    const onFocus = useEventCallback((
        e: FocusEvent<HTMLInputElement>
    ) => {
        if (e.currentTarget.value === '') {
            propsOnChange(e, '', null)
        }
        propsOnFocus?.(e)
    })

    // Нажатие на поле ввода.
    const onClick = useEventCallback((
        e: MouseEvent<HTMLInputElement>
    ) => {
        // Смещение курсора, если клик был на decimal separator или thousands separator.
        const input = e.currentTarget
        if (input.selectionStart === input.selectionEnd) {
            const position = findCursorPosition(
                input.value,
                input.selectionStart ?? 0,
                minCursorPosition
            )
            input.setSelectionRange(position, position)
        }
        propsOnClick?.(e)
    })

    return (
        <Input
            {...otherProps}
            value={propsValue ? formatValue(propsValue, numeralFormat) : ''}
            allowedChars={regionConfig.moneyInput.allowedChars}
            inputMode={inputMode}
            onKeyDown={onKeyDown}
            onBeforeInput={onBeforeInput}
            onChange={onChange}
            onPaste={onPaste}
            onFocus={onFocus}
            onClick={onClick}
        >
            <InputAddonText>
                {NumbersService.currencySymbol}
            </InputAddonText>
        </Input>
    )
}

// Форматирование значения по шаблону.
function formatValue(value: string, numeralFormat: string): string {
    if (value.length === 0) {
        return ''
    }
    return numeral(value).format(numeralFormat)
}

// Очистка значения.
function cleanValue(value: string): number | null {
    if (value.length === 0) {
        return null
    }
    return numeral(value).value()
}

// Проверка, является ли символ разделителем.
function isHardcodedCharacter(char: string): boolean {
    return char === NumbersService.thousandsSeparator
}

// Поиск правильной позиции курсора.
function findCursorPosition(
    value: string,
    currentCursorPosition: number,
    minCursorPosition: number
): number {
    if (currentCursorPosition >= value.length) {
        return value.length
    }
    if (isHardcodedCharacter(value[currentCursorPosition])) {
        return currentCursorPosition + 1
    }
    if (currentCursorPosition <= minCursorPosition) {
        return minCursorPosition
    }
    return currentCursorPosition
}
